import React from 'react';
import { Shield, Target, Eye, FileCheck, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Seo from '../components/Seo';
import { images } from '../mock';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      <Seo
        title="About MoveraCare Mobility | Houston NEMT Provider"
        description="MoveraCare Services LLC (DBA MoveraCare Mobility) is a Greater Houston non-emergency medical transportation provider serving patients, healthcare organizations, Workers' Compensation programs, and NEMT networks."
        path="/about"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'About', url: '/about' }]}
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-black">
        <div className="absolute inset-0 opacity-30">
          <img src={images.hospital} alt="Healthcare Facility" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">About MoveraCare Mobility</h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Professional healthcare transportation infrastructure serving Texas medical facilities and patients
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Who We Are</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  MoveraCare Mobility is a professional non-emergency medical transportation (NEMT) provider serving the Greater Houston healthcare community. We specialize in reliable, safe, and compliant transportation services for hospitals, dialysis centers, nursing facilities, and managed care organizations across Harris, Fort Bend, and surrounding counties.
                </p>
                <p>
                  Our commitment extends beyond simply moving patients from point A to point B. We understand that medical transportation is a critical component of healthcare delivery, and we approach every trip with the professionalism and care it deserves.
                </p>
                <p>
                  With a focus on punctuality, safety, and compliance, MoveraCare Mobility serves as a dependable partner for healthcare facilities requiring consistent, high-quality transportation services.
                </p>
              </div>
            </div>
            <div>
              <img src={images.facility} alt="Professional Healthcare" className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2 border-yellow-400">
              <CardHeader>
                <div className="w-14 h-14 bg-yellow-400 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-black" />
                </div>
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To provide reliable, safe, and compliant non-emergency medical transportation services that enable healthcare facilities to deliver seamless patient care. We are committed to being the transportation partner that healthcare providers can depend on, every single time.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-yellow-400">
              <CardHeader>
                <div className="w-14 h-14 bg-yellow-400 rounded-lg flex items-center justify-center mb-4">
                  <Eye className="w-7 h-7 text-black" />
                </div>
                <CardTitle className="text-2xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To become Texas's most trusted healthcare transportation infrastructure provider, setting the standard for reliability, professionalism, and compliance in the NEMT industry. We envision a future where every patient receives timely, safe, and dignified transportation to their medical appointments.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Compliance & Safety */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Compliance & Safety Commitment</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We maintain the highest standards in licensing, insurance, and operational compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Shield className="w-10 h-10 text-yellow-400 mb-4" />
                <CardTitle>Licensing & Insurance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-700">
                  <li>• Fully licensed NEMT provider</li>
                  <li>• Comprehensive liability coverage</li>
                  <li>• Commercial auto insurance</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <FileCheck className="w-10 h-10 text-yellow-400 mb-4" />
                <CardTitle>Driver Standards</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-700">
                  <li>• Drivers assist passengers during transport</li>
                  <li>• Professional dispatch coordination</li>
                  <li>• Wheelchair and mobility needs confirmed at booking so we can safely serve the trip</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Building2 className="w-10 h-10 text-yellow-400 mb-4" />
                <CardTitle>Operational Standards</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-700">
                  <li>• HIPAA-conscious operations</li>
                  <li>• Wheelchair-accessible vehicles</li>
                  <li>• Dispatch-coordinated fleet</li>
                  <li>• Responsive Dispatch During Operating Hours</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Insurance Coverage */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Insurance Coverage Overview</h2>
              <p className="text-gray-300 text-lg mb-6">
                MoveraCare Mobility maintains comprehensive insurance coverage to protect our clients, patients, and operations.
              </p>
              <div className="space-y-4">
                <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4">
                  <h3 className="text-yellow-400 font-semibold mb-2">Commercial General Liability</h3>
                  <p className="text-gray-300 text-sm">Comprehensive coverage for bodily injury and property damage</p>
                </div>
                <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4">
                  <h3 className="text-yellow-400 font-semibold mb-2">Commercial Auto Insurance</h3>
                  <p className="text-gray-300 text-sm">Full coverage for all fleet vehicles and operations</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-400/5 border border-yellow-400/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-yellow-400 mb-6">Coverage Highlights</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Certificates of Insurance available upon request</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Additional insured endorsements available for facility contracts</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Competitive coverage limits meeting industry standards</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Annual policy reviews and updates</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
